import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import {
  buildBlenderChartOptions,
  describeBlenderDataError,
  formatBlenderTimestamp,
  inferBlenderType,
  parseBlenderDataResponse,
  type BlenderChannel,
  type BlenderChannelsResponse,
  type BlenderChartHandoff,
  type BlenderChartView,
  type BlenderData,
  type BlenderDataResponse,
} from "../utils/blenderChart.ts";

Highcharts.setOptions({
  time: {
    useUTC: false,
  },
} as any);

const backendUrl =
  process.env.REACT_APP_BACKEND_URL || "http://localhost:5000/api";

const getAuthToken = () =>
  localStorage.getItem("jwt") || localStorage.getItem("token");

// Zooming into a window shorter than this refetches it at the finest
// resolution the API allows (1s up to 3 days, same thresholds as /pumps).
const ONE_SECOND_MAX_RANGE_MS = 3 * 24 * 60 * 60 * 1000;
// Below this span the zoomed window is too small to be worth a refetch.
const MIN_ZOOM_REFETCH_MS = 60 * 1000;

type PopoutParams = {
  blender: string;
  startTime: number;
  endTime: number;
  channels: string[];
  type: "diesel" | "electric";
  fleet: string | null;
  handoff: string | null;
};

const readParams = (): PopoutParams => {
  const search = new URLSearchParams(window.location.search);
  const blender = (search.get("blender") || "").trim();
  return {
    blender,
    startTime: Number(search.get("start_time")),
    endTime: Number(search.get("end_time")),
    channels: (search.get("channels") || "")
      .split(",")
      .map((key) => key.trim())
      .filter(Boolean),
    type:
      search.get("type") === "electric" || search.get("type") === "diesel"
        ? (search.get("type") as "diesel" | "electric")
        : inferBlenderType(blender),
    fleet: search.get("fleet"),
    handoff: search.get("handoff"),
  };
};

const fetchBlenderData = async (
  blender: string,
  startTime: number,
  endTime: number,
  channels: string[],
  signal?: AbortSignal,
): Promise<BlenderData> => {
  const token = getAuthToken();
  const params = new URLSearchParams({
    start_time: String(startTime),
    end_time: String(endTime),
    channels: channels.join(","),
    resolution: "auto",
    format: "columnar",
  });
  const response = await axios.get<BlenderDataResponse>(
    `${backendUrl}/blenders/${encodeURIComponent(blender)}/data?${params.toString()}`,
    { headers: { Authorization: `Bearer ${token}` }, signal },
  );
  return parseBlenderDataResponse(response.data, {
    blenderId: blender,
    startTime,
    endTime,
    channels,
  });
};

const BlenderChartPopout: React.FC = () => {
  const params = useMemo(readParams, []);

  const [channelByKey, setChannelByKey] = useState<Map<string, BlenderChannel>>(
    () => new Map(),
  );
  // Data for the range the pop-out was opened with.
  const [baseData, setBaseData] = useState<BlenderData | null>(null);
  // Finer-resolution data for a zoomed window, when one has been loaded.
  const [zoomData, setZoomData] = useState<BlenderData | null>(null);
  // The box the user zoomed to; null shows the full range.
  const [view, setView] = useState<BlenderChartView | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");

  const chartAreaRef = useRef<HTMLDivElement | null>(null);
  const [chartHeight, setChartHeight] = useState(0);
  const zoomRequestRef = useRef<AbortController | null>(null);

  useEffect(() => {
    document.title = params.blender
      ? `${params.blender} • Blender Graph`
      : "Blender Graph";
  }, [params.blender]);

  // Keep the chart filling the window as it is resized.
  useEffect(() => {
    const area = chartAreaRef.current;
    if (!area) return;
    const observer = new ResizeObserver((entries) => {
      const height = Math.floor(entries[0]?.contentRect.height ?? 0);
      setChartHeight(height);
    });
    observer.observe(area);
    return () => observer.disconnect();
  }, []);

  // Initial load: take the opener's data when it matches, otherwise fetch the
  // channel catalog and data (e.g. after the pop-out is refreshed).
  useEffect(() => {
    const { blender, startTime, endTime, channels } = params;
    if (
      !blender ||
      channels.length === 0 ||
      !Number.isFinite(startTime) ||
      !Number.isFinite(endTime) ||
      startTime >= endTime
    ) {
      setError("This graph link is missing its blender, channels or time range.");
      setIsLoading(false);
      return;
    }

    try {
      const handoff = (window.opener as any)?.__blenderChartHandoff as
        | BlenderChartHandoff
        | undefined;
      if (handoff && params.handoff && handoff.key === params.handoff) {
        setChannelByKey(
          new Map(handoff.channels.map((channel) => [channel.key, channel])),
        );
        setBaseData(handoff.data);
        setIsLoading(false);
        return;
      }
    } catch {
      // Cross-origin or closed opener: fall through to a normal fetch.
    }

    const controller = new AbortController();
    const token = getAuthToken();

    Promise.all([
      axios.get<BlenderChannelsResponse>(
        `${backendUrl}/blenders/${encodeURIComponent(blender)}/channels`,
        {
          headers: { Authorization: `Bearer ${token}` },
          signal: controller.signal,
        },
      ),
      fetchBlenderData(blender, startTime, endTime, channels, controller.signal),
    ])
      .then(([channelsResponse, data]) => {
        const byKey = new Map<string, BlenderChannel>();
        (channelsResponse.data?.groups ?? []).forEach((group) =>
          group.channels.forEach((channel) => byKey.set(channel.key, channel)),
        );
        setChannelByKey(byKey);
        setBaseData(data);
        setIsLoading(false);
      })
      .catch((err) => {
        if (axios.isCancel(err)) return;
        console.error("Failed to load blender pop-out chart:", err);
        setError(
          describeBlenderDataError(
            axios.isAxiosError(err) ? err.response?.data : null,
          ),
        );
        setIsLoading(false);
      });

    return () => controller.abort();
  }, [params]);

  const displayedData = zoomData ?? baseData;

  // Show the selected box straight away from the data already loaded, and
  // refetch it when the API can serve that window at a finer resolution.
  // Zooming inside 1s data needs no request.
  const handleSelectView = useCallback(
    (nextView: BlenderChartView) => {
      setView(nextView);

      const current = zoomData ?? baseData;
      if (!current || current.resolution === "1s") return;

      const min = nextView.xMin;
      const max = nextView.xMax;
      const span = max - min;
      if (span < MIN_ZOOM_REFETCH_MS) return;
      const finerResolution =
        span <= ONE_SECOND_MAX_RANGE_MS ? "1s" : current.resolution;
      if (finerResolution === current.resolution) return;

      zoomRequestRef.current?.abort();
      const controller = new AbortController();
      zoomRequestRef.current = controller;
      setIsLoading(true);
      setError("");

      fetchBlenderData(
        params.blender,
        Math.floor(min),
        Math.ceil(max),
        current.channels,
        controller.signal,
      )
        .then((data) => {
          setZoomData(data);
          setIsLoading(false);
        })
        .catch((err) => {
          if (axios.isCancel(err)) return;
          console.error("Failed to load zoomed blender data:", err);
          setError(
            describeBlenderDataError(
              axios.isAxiosError(err) ? err.response?.data : null,
            ),
          );
          setIsLoading(false);
        });
    },
    [baseData, zoomData, params.blender],
  );

  const resetZoom = () => {
    zoomRequestRef.current?.abort();
    setView(null);
    setZoomData(null);
    setIsLoading(false);
    setError("");
  };

  const chartOptions = useMemo(
    () =>
      buildBlenderChartOptions(displayedData, channelByKey, {
        height: chartHeight > 0 ? chartHeight : null,
        showTitle: false,
        view,
        onSelectView: handleSelectView,
      }),
    [displayedData, channelByKey, chartHeight, view, handleSelectView],
  );

  const rangeText = displayedData
    ? `${formatBlenderTimestamp(view?.xMin ?? displayedData.startTime)} – ${formatBlenderTimestamp(view?.xMax ?? displayedData.endTime)}`
    : "";

  return (
    <div className="chart-popout-page">
      <header className="chart-popout-header">
        <div className="chart-popout-title">
          <span className="chart-popout-fleet-chip">{params.blender}</span>
          <span className="chart-popout-pad">
            {params.type === "electric" ? "Electric" : "Diesel"} Blender
            {params.fleet ? ` • Fleet ${params.fleet}` : ""}
          </span>
          {rangeText && (
            <>
              <span className="chart-popout-divider" />
              <span className="chart-popout-well-chip">{rangeText}</span>
            </>
          )}
        </div>
        <div className="chart-popout-severity">
          {displayedData && (
            <span>
              {displayedData.resolution} resolution •{" "}
              {displayedData.rowCount.toLocaleString("en-US")} rows
            </span>
          )}
          {view && (
            <button
              type="button"
              className="chart-popout-reset-zoom"
              onClick={resetZoom}
            >
              Reset zoom
            </button>
          )}
        </div>
      </header>

      <div className="chart-popout-chart" ref={chartAreaRef}>
        {chartOptions && chartHeight > 0 && (
          <HighchartsReact
            highcharts={Highcharts}
            immutable={true}
            options={chartOptions}
          />
        )}

        {(isLoading || error || (!chartOptions && displayedData)) && (
          <div
            role="status"
            aria-live="polite"
            className="chart-popout-overlay"
            style={{
              background: chartOptions ? "rgba(48, 51, 53, 0.82)" : "#303335",
            }}
          >
            <div className="chart-popout-overlay-card">
              {isLoading ? (
                <h2>Loading blender data...</h2>
              ) : error ? (
                <>
                  <h2>Could not load blender data</h2>
                  <p>{error}</p>
                  {zoomData === null && baseData && (
                    <button
                      type="button"
                      className="chart-popout-reset-zoom"
                      onClick={() => setError("")}
                    >
                      Back to graph
                    </button>
                  )}
                </>
              ) : (
                <>
                  <h2>No data in this time range</h2>
                  <p>
                    {params.blender} did not report the selected channels in
                    this window.
                  </p>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BlenderChartPopout;
